import React, { useEffect, useState, useMemo } from "react";
import { Card, Form, Button, DatePicker, Table, Space, message, Row, Col, Typography, Tooltip } from "antd";
import { FileExcelOutlined, SearchOutlined, FilePdfOutlined } from "@ant-design/icons";
import apiClient from "../helpers/apiHelper";
import { ENDPOINTS, REQUEST_TYPES, MESSAGES } from "../helpers/apiConfig";
import { REPORT_TYPE_OPTIONS, vehicleReportColumns } from "../constants";
import { FilterSelect } from "../components/DashboardFilterBar";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export const ReportPage = ({ user }) => {
    const [form] = Form.useForm();
    const [vehicles, setVehicles] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [hasPreviewed, setHasPreviewed] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);

    const userRole = user?.role;

    const fetchVehicles = async () => {
        try {
            const res = await apiClient(ENDPOINTS.EQUIPMENT.FETCH_EQUIPMENTS, {
                method: "POST",
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_EQUIPMENTS,
                }),
            });

            const equipmentList = res?.items || [];
            const vehicleOptions = Array.from(
                new Set(equipmentList.map((e) => e.vehicle_no).filter(Boolean))
            )
                .sort()
                .map((v) => ({ label: v, value: v }));

            setVehicles(vehicleOptions);
        } catch (error) {
            message.error(MESSAGES.ERROR.FETCH_EQUIPMENTS);
        }
    };

    const buildReportBody = (values, extra = {}) => {
        const { date_range, vehicle_filter } = values || {};

        if (!date_range) {
            message.error("Please select a date range.");
            return null;
        }
        if (!vehicle_filter) {
            message.error("Please select a vehicle.");
            return null;
        }

        const [start, end] = date_range;

        return {
            request_type: REQUEST_TYPES.GENERATE_EQUIPMENT_REPORT,
            start_date: start.format("YYYY-MM-DD"),
            end_date: end.format("YYYY-MM-DD"),
            vehicle_no: vehicle_filter,
            ...extra,
        };
    };

    const generateReport = async (values, file_type = "excel") => {
        const isPdf = file_type === "pdf";
        isPdf ? setPdfLoading(true) : setExcelLoading(true);

        try {
            const body = buildReportBody(values, { preview: false, file_type });
            if (!body) return;

            const response = await apiClient(ENDPOINTS.REPORT.GENERATE_REPORT, {
                method: "POST",
                body: JSON.stringify(body),
                responseType: "blob",
            });

            if (response instanceof Blob && response.type === "application/json") {
                const text = await response.text();
                const errorData = JSON.parse(text);
                if (errorData.error) {
                    message.error(errorData.error);
                    return;
                }
            }

            const mimeType = isPdf
                ? "application/pdf"
                : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            const extension = isPdf ? "pdf" : "xlsx";

            const blob = new Blob([response], { type: mimeType });
            const href = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = href;
            a.download = `Vehicle_${body.vehicle_no}_${body.start_date}.${extension}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(href);

            message.success(MESSAGES.SUCCESS.GENERATE_REPORT);
        } catch (err) {
            message.error(err?.message || MESSAGES.ERROR.GENERATE_REPORT);
        } finally {
            isPdf ? setPdfLoading(false) : setExcelLoading(false);
        }
    };

    const previewReport = async (values) => {
        setTableLoading(true);
        try {
            const body = buildReportBody(values, { preview: true });
            if (!body) return;

            setHasPreviewed(true);

            const response = await apiClient(ENDPOINTS.REPORT.GENERATE_REPORT, {
                method: "POST",
                body: JSON.stringify(body),
            });

            if (response?.error) {
                message.error(response.error);
                setReportData([]);
                return;
            }

            setReportData(Array.isArray(response) ? response : response?.items || []);
        } catch (err) {
            message.error(err?.message || MESSAGES.ERROR.GENERATE_REPORT);
            setReportData([]);
        } finally {
            setTableLoading(false);
        }
    };
    
    const columns = useMemo(
        () =>
            vehicleReportColumns.map((col) => ({
                ...col,
                ellipsis: { showTitle: false },
                render: (text, record, index) => {
                    const display = col.render
                        ? col.render(text, record, index)
                        : text;
                    return (
                        <Tooltip placement="topLeft" title={display}>
                            {display}
                        </Tooltip>
                    );
                },
            })),
        []
    );

    useEffect(() => {
        fetchVehicles();
    }, []);

    return (
        <div>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card title={<Title level={4} style={{ marginBottom: 0 }}>Generate Reports</Title>}>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={(values) => generateReport(values, "excel")}
                            className="max-w-4xl"
                        >
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="report_type"
                                        label="Report Type"
                                        initialValue="vehicle"
                                    >
                                        <FilterSelect
                                            mode="default"
                                            placeholder="Select Report Type"
                                            options={REPORT_TYPE_OPTIONS}
                                            width="100%"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="vehicle_filter"
                                        label="Vehicle"
                                        rules={[{ required: true, message: "Please select a vehicle" }]}
                                    >
                                        <FilterSelect
                                            mode="default"
                                            placeholder="Select Vehicle"
                                            options={vehicles}
                                            width="100%"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        className="red-picker-scope"
                                        name="date_range"
                                        label="Date Range"
                                        rules={[{ required: true, message: "Please select date range!" }]}
                                    >
                                        <RangePicker className="w-full red-picker" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Space className="pt-[30px]" wrap>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<FileExcelOutlined />}
                                            loading={excelLoading}
                                            danger
                                        >
                                            Download Excel
                                        </Button>
                                        <Button
                                            type="primary"
                                            icon={<FilePdfOutlined />}
                                            loading={pdfLoading}
                                            onClick={() => {
                                                form.validateFields()
                                                    .then((values) => generateReport(values, "pdf"))
                                                    .catch(() => {});
                                            }}
                                            danger
                                        >
                                            Download PDF
                                        </Button>
                                        <Button
                                            onClick={() => previewReport(form.getFieldsValue())}
                                            icon={<SearchOutlined />}
                                            loading={tableLoading}
                                        >
                                            Preview
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </Col>
            </Row>

            {hasPreviewed && (
                <Row gutter={[16, 16]} className="mt-6">
                    <Col span={24}>
                        <Card title="Report Preview">
                            <Table
                                columns={columns}
                                dataSource={reportData}
                                rowKey={(record, index) => `${record["Vehicle No"]}_${record.Date}_${index}`}
                                loading={tableLoading}
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: "max-content" }}
                                locale={{
                                    emptyText: hasPreviewed
                                        ? "No records found for the selected range"
                                        : undefined,
                                }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};
